/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 */

import React, { useState, useEffect } from 'react';
import './Subscription.css';

// SVG Checkmark Icon
const CheckIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#4CAF50', marginRight: '10px' }}>
        <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
);

// Legal Modal Component
const LegalModal = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Terms of Service & Legal Disclaimer</h2>
                    <button className="modal-close" onClick={onClose}>&times;</button>
                </div>
                <div className="modal-body">
                    <h3>Terms of Service</h3>
                    <p>By subscribing to Marksure, you agree to the following terms:</p>
                    <ul>
                        <li>This is a private educational tool provided by Marksure (Pty) Ltd.</li>
                        <li>The service is not affiliated with or endorsed by the Gauteng Department of Education (GDE) or any other government entity.</li>
                        <li>All data entered remains your property and responsibility.</li>
                        <li>You are responsible for ensuring compliance with all applicable laws and regulations.</li>
                        <li>Service availability and features may change without notice.</li>
                    </ul>

                    <h3>Legal Disclaimer</h3>
                    <p><strong>Indemnity:</strong> You hereby indemnify and hold harmless Marksure (Pty) Ltd, its directors, employees, and agents from any claims, damages, losses, or liabilities arising from your use of this service.</p>
                    <p><strong>Data Responsibility:</strong> You acknowledge that this tool is not an official system of the Department of Basic Education or SASAMS. Users are responsible for verifying all data against official records.</p>
                    <p><strong>No Warranty:</strong> This service is provided "as is" without warranty of any kind, express or implied.</p>

                    <h3>Payment Terms</h3>
                    <p>All payments are final and non-refundable. Subscription fees are billed according to the selected plan.</p>
                </div>
                <div className="modal-footer">
                    <button className="btn-secondary" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
};

const Subscription = () => {
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [showRegistration, setShowRegistration] = useState(false);
    const [registrationData, setRegistrationData] = useState({
        email: '',
        password: '',
        schoolName: '',
        plan: ''
    });
    const [registrationLoading, setRegistrationLoading] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [showLegalModal, setShowLegalModal] = useState(false);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const status = urlParams.get('status');
        const plan = urlParams.get('plan');

        if (status === 'success') {
            setShowRegistration(true);
            setRegistrationData(prev => ({ ...prev, plan: plan || 'pro' }));
        } else if (status === 'cancelled') {
            setMessage('Payment was cancelled.');
        } else if (status === 'failed') {
            setMessage('Payment failed. Please try again.');
        }

        if (status) {
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, []);

    const handleSubscribe = async (plan) => {
        if (!termsAccepted) {
            alert('Please accept the Terms of Service before subscribing.');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/create_public_subscription.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ plan: plan.id }),
            });

            const result = await response.json();

            if (response.ok && result.success) {
                window.location.href = result.redirectUrl;
            } else {
                alert('Failed to create checkout: ' + (result.error || 'Unknown error'));
            }
        } catch (error) {
            alert('Error: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRegistration = async (e) => {
        e.preventDefault();
        setRegistrationLoading(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/complete_subscription.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(registrationData),
            });

            const result = await response.json();

            if (response.ok && result.success) {
                alert(`Account created successfully! Your username is: ${result.username}\n\nYou can now log in with your email and password.`);
                window.location.href = '/';
            } else {
                alert('Failed to create account: ' + (result.error || 'Unknown error'));
            }
        } catch (error) {
            alert('Error: ' + error.message);
        } finally {
            setRegistrationLoading(false);
        }
    };

    const handleRegistrationChange = (e) => {
        const { name, value } = e.target;
        // Validation logic
        if ((name === 'email' || name === 'schoolName') && /\d{13,19}/.test(value.replace(/\s/g, ''))) {
            alert(`Please enter a valid ${name === 'email' ? 'email' : 'school name'}, not a credit card number.`);
            return;
        }
        setRegistrationData({ ...registrationData, [name]: value });
    };

    const plans = [
        {
            id: 'basic',
            name: 'Basic',
            price: 85,
            period: 'month',
            features: ['Unlimited classes', 'Standard support', 'Export to Excel'],
            highlight: false
        },
        {
            id: 'pro',
            name: 'Pro',
            price: 199,
            period: 'term',
            features: ['Unlimited classes', 'Priority support', 'Export to Excel', 'Advanced Analytics'],
            highlight: true
        },
        {
            id: 'yearly',
            name: 'Yearly',
            price: 699,
            period: 'year',
            features: ['Unlimited classes', 'Priority support', 'Export to Excel', 'Advanced Analytics', 'Best value: Save 30%'],
            highlight: false
        }

    ];

    // --- RENDER REGISTRATION ---
    if (showRegistration) {
        return (
            <div className="sub-wrapper">
                <div className="sub-card registration-card">
                    <div className="header-section">
                        <h1>Setup Account</h1>
                        <p>Payment successful! Create your login details.</p>
                    </div>

                    <form onSubmit={handleRegistration} className="modern-form">
                        <div className="input-group">
                            <label>School Name</label>
                            <input
                                type="text"
                                name="schoolName"
                                value={registrationData.schoolName}
                                onChange={handleRegistrationChange}
                                required
                                placeholder="e.g. Pretoria High"
                            />
                        </div>

                        <div className="input-group">
                            <label>Email Address</label>
                            <input
                                type="email"
                                name="email"
                                value={registrationData.email}
                                onChange={handleRegistrationChange}
                                required
                                placeholder="name@school.co.za"
                            />
                        </div>

                        <div className="input-group">
                            <label>Password</label>
                            <input
                                type="password"
                                name="password"
                                value={registrationData.password}
                                onChange={handleRegistrationChange}
                                required
                                placeholder="Min 6 characters"
                                minLength="6"
                            />
                        </div>

                        <button type="submit" disabled={registrationLoading} className="btn-primary full-width">
                            {registrationLoading ? 'Creating...' : 'Finalize Account'}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    // --- RENDER PRICING PLANS ---
    return (
        <div className="sub-wrapper">
            <div className="sub-container">
                <div className="header-section center">
                    <h1>Choose Your Plan</h1>
                    <p>Simple, transparent pricing for South African educators.</p>
                </div>

                {message && (
                    <div className={`alert-banner ${message.includes('successful') ? 'success' : 'error'}`}>
                        {message}
                    </div>
                )}

                {/* Terms of Service Section */}
                <div className="terms-section">
                    <div className="terms-checkbox">
                        <input
                            type="checkbox"
                            id="terms-accept"
                            checked={termsAccepted}
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                        />
                        <label htmlFor="terms-accept">
                            I agree to the{' '}
                            <button
                                type="button"
                                className="link-button"
                                onClick={() => setShowLegalModal(true)}
                            >
                                Terms of Service
                            </button>{' '}
                            and acknowledge that this is a private tool, not affiliated with the GDE.
                        </label>
                    </div>
                </div>

                <div className="plans-grid">
                    {plans.map((plan) => (
                        <div key={plan.id} className={`plan-card ${plan.highlight ? 'highlight' : ''}`}>
                            {plan.highlight && <div className="popular-badge">Most Popular</div>}
                            <div className="plan-header">
                                <h2>{plan.name}</h2>
                                <div className="price-wrapper">
                                    <span className="currency">R</span>
                                    <span className="amount">{plan.price}</span>
                                    <span className="period">/{plan.period}</span>
                                </div>
                            </div>

                            <ul className="feature-list">
                                {plan.features.map((feature, index) => (
                                    <li key={index}><CheckIcon /> {feature}</li>
                                ))}
                            </ul>

                            <button
                                onClick={() => handleSubscribe(plan)}
                                disabled={loading || !termsAccepted}
                                className={`btn-primary full-width ${plan.highlight ? '' : 'outline'} ${!termsAccepted ? 'disabled' : ''}`}
                            >
                                {loading ? 'Processing...' : 'Subscribe Now'}
                            </button>
                        </div>
                    ))}
                </div>

                <div className="bottom-link">
                    <p>Already have an account? <a href="/">Log in here</a></p>
                </div>
            </div>

            {/* Legal Modal */}
            <LegalModal isOpen={showLegalModal} onClose={() => setShowLegalModal(false)} />
        </div>
    );
};

export default Subscription;